import React, { FC } from "react";
import { Button } from "@telia/teddy";
import _get from "lodash/get";
import _isEmpty from "lodash/isEmpty";
import { connect, useDispatch, useSelector } from "react-redux";
import { DeliveryType, PAYMENT_METHOD, PaymentMethod } from "../../../../../../constants";
import { RootState } from "../../../../../../models";
import * as LOADING_SELECTORS from "../../../../loading/state/selectors";
import * as NAVIGATION_SELECTORS from "../../../../navigation/state/selectors";
import * as VOUCHER_SELECTORS from "../../../../voucher/state/selectors";
import { checkoutPayment, closeDelayOnPurchaseModal } from "../../../state/actions/paymentActions";
import * as CHECKOUT_AT from "../../../state/actionTypes";
import { getTotalPriceUpfront } from "../../../state/selectors/cartSelectors";
import * as COMBINED_SELECTORS from "../../../state/selectors/combinedSelectors";
import {
    getDeliveryInfoCode,
    getDeliveryInfoNote,
    getDeliveryInfoType,
    getEnhancedDeliveryInfoSelected,
} from "../../../state/selectors/deliveryInfoSelectors";
import { getPaymentSelected, getPaymentSelectedType } from "../../../state/selectors/paymentSelectors";
import TEXT from "../../../text-consts";

interface StateProps {
    deliveryInfoCode?: string;
    deliveryInfoNote?: string;
    deliveryInfoType?: DeliveryType;
    hasReachedMaximumNumberOfSubscriptions: boolean;
    isDigitalDelivery: boolean;
    isLoading: boolean;
    isNavigating: boolean;
    isSubmittingOrder: boolean;
    selectedPaymentType?: PaymentMethod;
}

interface Props extends StateProps {
    hasSeenDelayOnPurchaseModal?: boolean;
}

const SummaryContinueButton: FC<Props> = ({
    isSubmittingOrder,
    hasReachedMaximumNumberOfSubscriptions,
    isLoading,
    isNavigating,
    deliveryInfoType,
    deliveryInfoCode,
    deliveryInfoNote,
    selectedPaymentType,
    hasSeenDelayOnPurchaseModal = false,
    isDigitalDelivery,
}) => {
    const dispatch = useDispatch();

    const onSubmit = (): void => {
        if (hasSeenDelayOnPurchaseModal) {
            dispatch(closeDelayOnPurchaseModal());
        }

        dispatch(
            checkoutPayment(
                deliveryInfoType,
                deliveryInfoCode,
                deliveryInfoNote,
                selectedPaymentType,
                undefined,
                undefined,
                hasSeenDelayOnPurchaseModal,
            ),
        );
    };

    const isInvoice = selectedPaymentType === PAYMENT_METHOD.INVOICE;
    const totalPriceUpfront = useSelector((state: RootState) => getTotalPriceUpfront(state));
    const voucherDiscount = useSelector((state: RootState) => VOUCHER_SELECTORS.getTotalVoucherDiscount(state));
    const paymentMethod = useSelector((state: RootState) => getPaymentSelected(state));
    const deliveryMethod = useSelector((state: RootState) => getEnhancedDeliveryInfoSelected(state));
    const customerHasToPayNow =
        totalPriceUpfront - voucherDiscount + _get(deliveryMethod, "price", 0) > 0 && !isInvoice;
    const buttonText = customerHasToPayNow ? TEXT.SUMMARY.GO_TO_PAYMENT : TEXT.SUMMARY.ORDER;
    const hasNoPaymentAndDeliveryMethod = _isEmpty(paymentMethod) && _isEmpty(deliveryMethod) && !isDigitalDelivery;

    return (
        <Button
            className="beta-checkout__continue-button-container__button"
            id="contact-info-form-button"
            onClick={onSubmit}
            loading={isSubmittingOrder || isNavigating}
            type="submit"
            disabled={
                hasReachedMaximumNumberOfSubscriptions || isLoading || isNavigating || hasNoPaymentAndDeliveryMethod
            }
        >
            {buttonText}
        </Button>
    );
};

const mapStateToProps = (state: RootState): StateProps => ({
    isSubmittingOrder: LOADING_SELECTORS.isTypeLoading(state, CHECKOUT_AT.CHECKOUT_PAYMENT),
    hasReachedMaximumNumberOfSubscriptions: COMBINED_SELECTORS.hasReachedMaximumNumberOfSubscriptions(state),
    isLoading: LOADING_SELECTORS.isLoading(state),
    deliveryInfoType: getDeliveryInfoType(state),
    deliveryInfoCode: getDeliveryInfoCode(state),
    deliveryInfoNote: getDeliveryInfoNote(state),
    selectedPaymentType: getPaymentSelectedType(state),
    isNavigating: NAVIGATION_SELECTORS.isNavigating(state),
    isDigitalDelivery: COMBINED_SELECTORS.isDigitalDelivery(state),
});

export default connect(mapStateToProps, {
    checkoutPayment,
})(SummaryContinueButton);
